import React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import VerifiedIcon from '@mui/icons-material/Verified';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import Typography from '@mui/material/Typography';
import type { EditorMode } from './ModeTabs';

interface ActionButtonsProps {
  mode: EditorMode;
  onGenerate: () => void;
  onApply: () => void;
  onVerify: () => void;
  onReset: () => void;
  generateLabel?: string;
  generateHint?: string;
}

const MODE_CONFIG: Record<EditorMode, { label: string; icon: React.ReactNode; hint: string }> = {
  generate: {
    label: 'Generate Patch',
    icon: <AutoFixHighIcon />,
    hint: 'Output appears below - copy and share live under the patch panel.'
  },
  apply: {
    label: 'Apply Patch',
    icon: <PlayArrowIcon />,
    hint: 'The result appears below, ready to copy.'
  },
  verify: {
    label: 'Verify Patch',
    icon: <VerifiedIcon />,
    hint: 'Checks that applying the patch to Source reproduces Target exactly.'
  }
};

const ActionButtons = ({ mode, onGenerate, onApply, onVerify, onReset, generateLabel, generateHint }: ActionButtonsProps) => {
  const baseConfig = MODE_CONFIG[mode] ?? MODE_CONFIG.generate;
  const config = mode === 'generate'
    ? { ...baseConfig, label: generateLabel ?? baseConfig.label, hint: generateHint ?? baseConfig.hint }
    : baseConfig;
  const isMac = typeof navigator !== 'undefined' && /mac/i.test(navigator.platform);
  const runShortcut = isMac ? '⌘↵' : 'Ctrl+↵';

  const handlePrimary = () => {
    if (mode === 'generate') onGenerate();
    else if (mode === 'apply') onApply();
    else onVerify();
  };

  return (
    <Box sx={{ mb: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <Button
          variant="contained"
          startIcon={config.icon}
          onClick={handlePrimary}
          className="pill-button-primary"
        >
          {config.label}
        </Button>
        <Tooltip title="Clear all editors" arrow>
          <IconButton
            size="medium"
            onClick={onReset}
            className="editor-panel-icon-button"
            aria-label="Reset all editors"
          >
            <RestartAltIcon />
          </IconButton>
        </Tooltip>
      </Box>
      <Typography
        variant="body2"
        sx={{
          mt: 1,
          mb: 2,
          textAlign: 'center',
          color: 'var(--fg-muted)',
          fontSize: '0.78rem'
        }}
      >
        {config.hint} Press {runShortcut} to run.
      </Typography>
    </Box>
  );
};

export default ActionButtons;
